import React from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, Shield, Globe, Layers, Users, ArrowDown } from 'lucide-react'
import AxonallLogo from '../components/AxonallLogo'

const DARK = '#111111'
const CHARCOAL = '#3a3a3a'
const LIGHT_BG = '#f7f7f7'

const LandingPage = () => {
  const navigate = useNavigate()
  // navigate used for /register

  const handleApplyClick = () => {
    navigate('/luxurytravel-advisors')
  }

  const features = [
    {
      icon: <Shield className="h-5 w-5" style={{ color: CHARCOAL }} />,
      title: 'Secure by Design',
      description:
        'Search and book Axonall product, manage and maintain your activities, and support client bookings within a protected environment designed for secure access and safeguarded design.'
    },
    {
      icon: <Globe className="h-5 w-5" style={{ color: CHARCOAL }} />,
      title: 'Curated Global Access',
      description:
        'Explore a more selective world of luxury stays, experiences, and differentiated travel opportunities across global destinations, with access to off-market stays and selected Axonall-exclusive travel opportunities for Reserve members.'
    },
    {
      icon: <Layers className="h-5 w-5" style={{ color: CHARCOAL }} />,
      title: 'Unified Access',
      description:
        'Access accommodation, flights, experiences, and other Axonall product through one login, in one connected environment designed to simplify how you work.'
    },
    {
      icon: <Users className="h-5 w-5" style={{ color: CHARCOAL }} />,
      title: 'Human Support, When It Matters',
      description:
        "Every booking is supported by Axonall's Journey Artisan team, adding a high-touch layer of care, coordination, and human judgement for you and your clients throughout the journey."
    }
  ]

  const benefits = [
    'Access to ultra-luxury and differentiated travel product',
    'Real-time availability and faster booking',
    'Training, briefings, and member resources',
    'Intelligent tools to support itinerary creation and trip design',
    'Clearer visibility across bookings and account activity',
    'Journey Artisan support with every booking'
  ]

  return (
    <div className="min-h-screen" style={{ backgroundColor: LIGHT_BG }}>

      {/* ═══════════════════════════════════════════════════════
          HEADER — sticky single-row bar
      ═══════════════════════════════════════════════════════ */}
      <header
        className="sticky top-0 z-50 bg-white"
        style={{ boxShadow: '0 1px 0 rgba(0,0,0,0.06)' }}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-10 lg:px-16 flex items-center justify-between h-[60px]">
          {/* Logo — left */}
          <AxonallLogo size="text-lg sm:text-xl" color="text-gray-900" />

          {/* Nav — right */}
          <nav className="flex items-center gap-6 sm:gap-8 lg:gap-10">
            {/* Text links hidden on mobile, visible sm+ */}
            <button
              onClick={() => navigate('/login')}
              className="hidden sm:block text-gray-500 hover:text-gray-900 text-[10px] tracking-[0.2em] uppercase font-poppinsRegular transition-colors duration-200"
            >
              Sign In
            </button>
            <button
              onClick={handleApplyClick}
              className="hidden sm:block text-gray-500 hover:text-gray-900 text-[10px] tracking-[0.2em] uppercase font-poppinsRegular transition-colors duration-200"
            >
              Get Started
            </button>
            {/* Primary CTA — always visible */}
            <button
              onClick={() => navigate('/login')}
              className="text-[10px] tracking-[0.2em] uppercase font-poppinsRegular py-2 px-4 sm:px-5 transition-colors duration-200 whitespace-nowrap"
              style={{ backgroundColor: '#111111', color: 'white' }}
            >
              Enter Studio
            </button>
          </nav>
        </div>
      </header>

      {/* ═══════════════════════════════════════════════════════
          HERO — full-viewport, gradient overlay, scroll hint
      ═══════════════════════════════════════════════════════ */}
      <section
        className="relative flex flex-col items-center justify-center"
        style={{
          backgroundImage: `url('https://s3.eu-north-1.amazonaws.com/spaceaxonall.com/origin+mvp+assets/landing_page/axonall-coromandel.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          minHeight: 'calc(100dvh - 60px)'
        }}
      >
        {/* Layered gradient overlay — richer depth than flat black */}
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.62) 50%, rgba(0,0,0,0.82) 100%)'
        }} />

        <div className="relative z-10 text-center px-5 sm:px-6 lg:px-8 w-full max-w-5xl mx-auto py-16 sm:py-24 animate-fade-in">
          {/* Eyebrow */}
          <p className="text-[10px] tracking-[0.5em] uppercase text-white/55 mb-5 font-poppinsLight">
            AXONALL STUDIO
          </p>

          {/* Thin decorative rule */}
          <div className="w-8 h-px bg-white/30 mx-auto mb-7" />

          {/* Main title */}
          <h1
            className="text-4xl sm:text-5xl md:text-[62px] lg:text-[72px] text-white font-AXONALLPrimeRegular tracking-[0.01em] sm:tracking-[0.04em] lg:tracking-[0.08em] uppercase leading-[1.1] mb-8"
          >
            Private Advisor<br />Circle
          </h1>

          <p className="text-sm text-white/70 mb-11 max-w-md mx-auto font-poppinsLight leading-relaxed tracking-wide">
            Access to the Studio is reserved for approved Axonall Reserve Advisors.
            If you need assistance, please contact us.
          </p>

          {/* Outlined CTA */}
          <button
            onClick={() => navigate('/login')}
            className="inline-flex items-center gap-3 border border-white/60 text-white hover:bg-white hover:text-gray-900 text-[11px] tracking-[0.2em] sm:tracking-[0.32em] uppercase font-poppinsRegular py-3.5 px-7 sm:px-10 transition-all duration-300"
          >
            Enter Studio
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>


      </section>

      {/* ═══════════════════════════════════════════════════════
          FEATURES — full-width split: dark left + light right
      ═══════════════════════════════════════════════════════ */}
      <section className="flex flex-col lg:flex-row overflow-hidden">

        {/* Left — dark panel with overlapping framed photos */}
        <div
          className="lg:w-[42%] relative min-h-[500px] lg:min-h-[680px] flex-shrink-0"
          style={{ backgroundColor: DARK }}
        >
          {/* Large photo — hover zooms */}
          <div
            className="absolute overflow-hidden shadow-xl"
            style={{
              top: '44px', left: '36px', right: '72px', bottom: '158px',
              border: '10px solid white'
            }}
          >
            <img
              src="https://d1su8gdu4l1hio.cloudfront.net/origin+mvp+assets/IDK-REQ/pexels-lina-12238284.jpg"
              alt="Luxury Travel Experience"
              className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
            />
          </div>

          {/* Small overlap photo — hover zooms */}
          <div
            className="absolute overflow-hidden shadow-xl"
            style={{
              bottom: '36px', right: '0', width: '54%', height: '230px',
              border: '10px solid white'
            }}
          >
            <img
              src="https://d1su8gdu4l1hio.cloudfront.net/origin+mvp+assets/IDK-REQ/atlantis-the-palmjpg-1b19e1d1-b721-471b-b737-a08aea6cf0f5.jpg"
              alt="Resort Experience"
              className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
            />
          </div>
        </div>

        {/* Right — content */}
        <div
          className="lg:w-[58%] flex items-center px-10 sm:px-14 lg:px-20 py-16"
          style={{ backgroundColor: LIGHT_BG }}
        >
          <div className="max-w-xl">
            <p className="text-[10px] tracking-[0.35em] uppercase font-poppinsLight mb-3" style={{ color: CHARCOAL }}>
              AXONALL STUDIO
            </p>
            <h2
              className="text-4xl sm:text-[46px] font-AXONALLPrimeLight tracking-wider uppercase leading-[1.08] mb-3"
              style={{ color: DARK }}
            >
              WHY AXONALL STUDIO?
            </h2>
            <div className="w-8 h-px mb-6" style={{ backgroundColor: CHARCOAL }} />
            <p className="font-poppinsLight text-gray-500 text-sm leading-relaxed mb-8">
              Axonall Studio is where you access curated product, manage your activity, and work
              within a more connected Axonall environment.
            </p>

            {/* Feature rows */}
            <div className="mb-10">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="flex items-start py-4 border-b border-gray-200/60 group"
                >
                  <div className="flex-shrink-0 mr-4 mt-0.5 opacity-70 group-hover:opacity-100 transition-opacity duration-200">
                    {feature.icon}
                  </div>
                  <div>
                    <h3
                      className="font-poppinsRegular text-[11px] tracking-[0.18em] uppercase mb-1.5"
                      style={{ color: DARK }}
                    >
                      {feature.title}
                    </h3>
                    <p className="font-poppinsLight text-gray-500 text-sm leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={() => navigate('/login')}
              style={{ backgroundColor: DARK }}
              className="text-white text-[11px] tracking-[0.3em] uppercase font-poppinsRegular py-4 px-10 hover:bg-black transition-colors duration-200"
            >
              Enter Studio
            </button>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          PANORAMIC BANNER + OVERLAPPING PHOTO SECTION
      ═══════════════════════════════════════════════════════ */}
      <div className="relative">

        {/* Parallax banner image */}
        <div
          className="w-full"
          style={{
            height: '280px',
            backgroundImage: `url('https://cdn.pixabay.com/photo/2023/03/29/10/27/hotel-7885138_1280.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center 30%',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed'
          }}
        />

        {/* Content section — right photo bleeds into the banner above */}
        <section style={{ backgroundColor: LIGHT_BG }} className="relative pt-10 pb-28">
          <div className="max-w-7xl mx-auto px-8 sm:px-12 lg:px-20">
            <div className="flex flex-col lg:flex-row gap-12 lg:gap-24 items-start">

              {/* Text content */}
              <div className="lg:w-1/2 pt-10 lg:pt-16">
                <p className="text-[10px] tracking-[0.35em] uppercase font-poppinsLight mb-3" style={{ color: CHARCOAL }}>
                  THE AXONALL APPROACH
                </p>
                <h2
                  className="text-4xl sm:text-[46px] font-AXONALLPrimeLight tracking-wider uppercase leading-[1.08] mb-3"
                  style={{ color: DARK }}
                >
                  CURATED WITH INTENTION, NOT EXCESS
                </h2>
                <div className="w-8 h-px mb-7" style={{ backgroundColor: CHARCOAL }} />
                <p className="font-poppinsLight text-gray-500 text-sm leading-relaxed mb-4">
                  We don't offer everything. We offer the right things.
                </p>
                <p className="font-poppinsLight text-gray-500 text-sm leading-relaxed mb-4">
                  Every property, experience, and opportunity inside Axonall Studio is reviewed
                  through a standard of quality over volume. Access is curated — and kept that way.
                </p>
                <p className="font-poppinsLight text-gray-500 text-sm leading-relaxed mb-10">
                  Our curation is ongoing. What's available today reflects our current standards,
                  and what's removed reflects our commitment to maintaining them.
                </p>
                <button
                  onClick={() => navigate('/login')}
                  style={{ backgroundColor: DARK }}
                  className="text-white text-[11px] tracking-[0.3em] uppercase font-poppinsRegular py-4 px-10 hover:bg-black transition-colors duration-200"
                >
                  Explore Studio
                </button>
              </div>

              {/* Overlapping photo */}
              <div className="lg:w-1/2 relative z-10" style={{ marginTop: '-155px' }}>
                <div className="overflow-hidden shadow-2xl" style={{ border: '10px solid white' }}>
                  <img
                    src="https://cdn.pixabay.com/photo/2015/12/29/18/25/dubai-1113262_1280.jpg"
                    alt="Curated Travel Experience"
                    className="w-full h-[560px] object-cover transition-transform duration-700 hover:scale-105"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* ═══════════════════════════════════════════════════════
          BENEFITS — two-column layout over parallax image
      ═══════════════════════════════════════════════════════ */}
      <section
        className="relative py-32"
        style={{
          backgroundImage: `url('https://d1su8gdu4l1hio.cloudfront.net/origin+mvp+assets/IDK-REQ/pexels-mikhail-nilov-8300820.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0 bg-black/60" />

        <div className="relative z-10 max-w-7xl mx-auto px-8 sm:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">

            {/* Left — heading + description + CTA */}
            <div>
              <p className="text-[10px] tracking-[0.38em] uppercase text-white/50 mb-4 font-poppinsLight">
                MEMBER ACCESS
              </p>
              <h2 className="text-4xl sm:text-5xl font-AXONALLPrimeRegular tracking-wider uppercase text-white leading-[1.08] mb-4">
                WHAT YOUR ACCESS INCLUDES
              </h2>
              <div className="w-8 h-px bg-white/30 mb-7" />
              <p className="font-poppinsLight text-white/70 text-sm leading-relaxed mb-10">
                As an Axonall Reserve member, your access includes a more elevated set of booking
                tools, Journey Artisan support, and opportunities designed to help you serve clients
                with greater distinction.
              </p>
              <button
                onClick={() => navigate('/login')}
                style={{ backgroundColor: CHARCOAL }}
                className="text-white text-[11px] tracking-[0.3em] uppercase font-poppinsRegular py-4 px-10 hover:bg-black transition-colors duration-200"
              >
                Enter Axonall Studio
              </button>
            </div>

            {/* Right — numbered benefits list */}
            <div className="space-y-0">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="flex items-start gap-5 py-4 border-b border-white/10 group"
                >
                  <span className="text-white/25 font-poppinsLight text-[11px] tracking-widest pt-0.5 flex-shrink-0 group-hover:text-white/50 transition-colors duration-200">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="text-white/80 font-poppinsLight text-sm leading-relaxed group-hover:text-white transition-colors duration-200">
                    {benefit}
                  </span>
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          FOOTER — CTA block + brand info + copyright
      ═══════════════════════════════════════════════════════ */}
      <footer style={{ backgroundColor: '#0a0a0a' }} className="text-white">

        {/* Top accent line */}
        <div className="h-px w-full" style={{ background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.15), transparent)' }} />

        {/* CTA block */}
        <div className="max-w-4xl mx-auto px-6 py-24 text-center">
          <p className="text-[10px] tracking-[0.42em] uppercase font-poppinsLight text-white/40 mb-5">
            YOUR ACCESS CONTINUES
          </p>
          <div className="w-8 h-px bg-white/20 mx-auto mb-7" />
          <h2 className="text-4xl sm:text-5xl md:text-[52px] font-AXONALLPrimeRegular tracking-wider uppercase text-white leading-[1.06] mb-10">
            YOUR ACCESS TO AXONALL CONTINUES HERE
          </h2>
          <button
            onClick={() => navigate('/login')}
            className="inline-flex items-center gap-3 border border-white/50 text-white hover:bg-white hover:text-gray-900 text-[11px] tracking-[0.3em] uppercase font-poppinsRegular py-4 px-12 transition-all duration-300"
          >
            Enter Axonall Studio
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Divider */}
        <div className="w-full border-t border-white/8" />

        {/* Brand info */}
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-12">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8 pb-10 border-b border-white/10">
            <div>
              <div className="mb-4">
                <AxonallLogo size="text-xl" color="text-white" />
              </div>
              <p className="font-poppinsLight text-white/40 text-sm max-w-xs leading-relaxed">
                The premier platform for luxury travel advisors, connecting you with the world's
                most exclusive destinations and experiences.
              </p>
            </div>
            <div className="flex gap-6 flex-shrink-0">
              <button
                onClick={handleApplyClick}
                className="text-white/40 hover:text-white text-[11px] tracking-[0.18em] uppercase font-poppinsLight transition-colors duration-200"
              >
                Apply Now
              </button>
              <button
                onClick={() => navigate('/login')}
                className="text-white/40 hover:text-white text-[11px] tracking-[0.18em] uppercase font-poppinsLight transition-colors duration-200"
              >
                Sign In
              </button>
            </div>
          </div>

          {/* Copyright */}
          <div className="pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="font-poppinsLight text-white/25 text-[11px] tracking-[0.12em]">
              © {new Date().getFullYear()} AXONALL Luxury Travel. All rights reserved.
            </p>
            <p className="font-poppinsLight text-white/20 text-[9px] tracking-wide">
              Private Advisor Circle
            </p>
          </div>
        </div>
      </footer>

    </div>
  )
}

export default LandingPage
